import { Button, SelectChangeEvent, TextField } from '@mui/material';
import Grid from '@mui/material/Grid';
import { ChangeEvent, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { ConfigContext } from '@/Contexts/ConfigContext';
import { LocaleContext } from '@/Contexts/LocaleContext';
import { SelectedProgramContext } from '@/Contexts/SelectedProgramContext';
import { UsersContext } from '@/Contexts/UsersContext';
import useContact from '@/hooks/useContact';
import useStatesAndCounties from '@/hooks/useStatesAndCounties';
import { AddressProps, LoqateAddressProps } from '@/models/Address';
import { CountyProps } from '@/models/County';
import { ProgramNames } from '@/models/Program';
import { UserType } from '@/models/User';
import { loqateApiKey, USCountryId } from '@/utils/constants';

import CharacterLimitHelperText from '../CharacterLimitHelperText';
import { SelectInput } from '../select_input/SelectInput';

interface LoqateControl {
  listen: (eventName: 'populate' | 'error', callback: (payload: unknown) => void) => void;
}

interface LoqateGlobal {
  Address: new (
    fields: Array<{ element: string; field: string; mode?: string | number }>,
    options: {
      key: string;
      suppressAutocomplete: boolean;
      countries: { codesList: string };
      setCountryByIP: boolean;
    }
  ) => LoqateControl;
  fieldMode: {
    POPULATE: string | number;
  };
}

declare global {
  interface Window {
    pca?: LoqateGlobal;
  }
}

function isLoqateAddress(payload: unknown): payload is LoqateAddressProps {
  return payload != null && typeof payload === 'object' && 'Province' in payload;
}

export default function Address({
  address: originalAddress,
  onChange,
  disabled,
  showErrors = false,
  hideCounty = false,
  id,
}: {
  address: AddressProps;
  onChange: (address: AddressProps) => void;
  disabled?: boolean;
  showErrors?: boolean;
  hideCounty?: boolean;
  id?: string;
}) {
  const { allStates, allCountries, countrySelectOptions, stateSelectOptions } =
    useContext(ConfigContext);
  const { userType } = useContext(UsersContext);

  const { selectedProgramId } = useContext(SelectedProgramContext);
  const { translatedText } = useContext(LocaleContext);
  const [address, setAddress] = useState<AddressProps>(originalAddress);
  const fieldIdPrefix = id ?? 'address';

  useEffect(() => {
    setAddress(originalAddress);
  }, [originalAddress]);

  const { getStateIdByStateAbbreviation, getCountiesInState, getCountryIdByCountryAbbreviation } =
    useStatesAndCounties();

  const { isZipInValid } = useContact();

  const [shippingCountiesInState, setShippingCountiesInState] = useState<CountyProps[]>([]);

  const shippingCountiesInStateOptions = useMemo(() => {
    return (shippingCountiesInState ?? []).sort((a: CountyProps, b: CountyProps) =>
      a.name > b.name ? 1 : b.name > a.name ? -1 : 0
    );
  }, [shippingCountiesInState]);

  const [loqateControl, setLoqateControl] = useState<LoqateControl | null>(null);
  const [showAddress2, setShowAddress2] = useState(false);

  const showCountyAndState = useMemo(() => {
    return address?.countryId === USCountryId && userType != UserType.NationalProgrammeLead;
  }, [address?.countryId, userType]);

  useEffect(() => {
    if (address?.streetAddress2 != null && address.streetAddress2 !== '') {
      setShowAddress2(true);
    }
  }, [address?.streetAddress2]);

  const updateZipCode = useCallback(
    (zipCode: string) => {
      const zipCodeWithoutSymbols = zipCode.replace(/\D/g, '');
      const zipCodeAsNumber =
        !isNaN(Number(zipCodeWithoutSymbols)) && zipCode !== '' ? zipCodeWithoutSymbols : null;

      const result =
        selectedProgramId != ProgramNames.TreeCitiesWorld || address?.countryId === USCountryId
          ? zipCodeAsNumber
          : zipCode.toUpperCase();

      return result;
    },
    [address?.countryId, selectedProgramId]
  );

  const handleAddressChange = useCallback(
    (key: keyof AddressProps, value: string | number | null | undefined) => {
      if (key === 'zipCode') {
        value = updateZipCode((value ?? '') as string);
      }

      const updatedAddress = { ...address, [key]: value };
      setAddress(updatedAddress);
      onChange(updatedAddress);
    },
    [address, onChange, updateZipCode]
  );

  const updateStateId = useCallback(
    (newStateId: string) => {
      // if the state changes, we need to reset the county
      const updatedAddress = { ...address, stateId: newStateId, countyId: null };
      setAddress(updatedAddress);
      onChange(updatedAddress);
    },
    [address, onChange]
  );

  const handleAddAddress2 = useCallback(() => {
    setShowAddress2(true);
  }, []);

  useEffect(() => {
    if (address?.stateId != null && address.stateId !== '') {
      const filteredCounties = getCountiesInState(address.stateId);
      setShippingCountiesInState(filteredCounties);
    } else {
      setShippingCountiesInState([]);
    }
  }, [getCountiesInState, address?.stateId]);

  const initializeLoqate = useCallback(() => {
    if (loqateControl != null) return;
    const pca = window.pca;
    if (pca == null) return;

    // pca is a global object created by the loqate library cich is loaded in index.html file
    const loqateFields = [
      { element: `${fieldIdPrefix}-streetAddress1`, field: 'Line1' },
      {
        element: `${fieldIdPrefix}-streetAddress2`,
        field: 'Line2',
        mode: pca.fieldMode.POPULATE,
      },
      { element: `${fieldIdPrefix}-city`, field: 'City', mode: pca.fieldMode.POPULATE },
      {
        element: `${fieldIdPrefix}-zipCode`,
        field: 'PostalCode',
        mode: pca.fieldMode.POPULATE,
      },
      { element: '', field: 'Company' }, // Make sure company is blank
      // we'll populate these manually
      // { element: "state-select", field: "Province", mode: pca.fieldMode.POPULATE },
      // { element: "county-select", field: "AdminAreaName", mode: pca.fieldMode.POPULATE },
    ];

    const options = {
      key: loqateApiKey,
      suppressAutocomplete: false,
      countries: { codesList: 'US' },
      setCountryByIP: true,
    };

    setLoqateControl(new pca.Address(loqateFields, options));
  }, [fieldIdPrefix, loqateControl]);

  const handleLoqatePopulate = useCallback(
    (loqateAddress: LoqateAddressProps) => {
      let zipCode: string = loqateAddress.PostalCode;

      if (zipCode !== '' && loqateAddress.CountryIso2 === 'US' && /^\d{5}-\d{4}$/.test(zipCode)) {
        zipCode = zipCode.substring(0, 5);
      }

      const stateId = getStateIdByStateAbbreviation(loqateAddress.Province, allStates);
      const countryId = getCountryIdByCountryAbbreviation(loqateAddress.CountryIso2, allCountries);

      const updatedAddress = {
        ...address,
        countyId: loqateAddress.AdminAreaCode,
        city: loqateAddress.City,
        zipCode: zipCode,
        streetAddress1: loqateAddress.Line1,
        streetAddress2: loqateAddress.Line2,
        stateId: stateId,
        countryId: countryId,
      } as AddressProps;

      onChange(updatedAddress);
      setAddress(updatedAddress);
    },
    [
      getStateIdByStateAbbreviation,
      getCountryIdByCountryAbbreviation,
      allStates,
      allCountries,
      address,
      onChange,
    ]
  );

  useEffect(() => {
    if (loqateControl != null) {
      loqateControl.listen('populate', (payload: unknown) => {
        if (isLoqateAddress(payload)) {
          handleLoqatePopulate(payload);
        }
      });
    }
  }, [handleLoqatePopulate, loqateControl]);

  useEffect(() => {
    initializeLoqate();
  }, [initializeLoqate]);

  const zipCodeError: boolean =
    showErrors &&
    (address?.zipCode == null ||
      address?.zipCode === '' ||
      address?.countryId == null ||
      isZipInValid(address.zipCode, address.countryId, selectedProgramId));

  return (
    <Grid container rowSpacing={2} columnSpacing={4}>
      <Grid size={12}>
        <TextField
          id={`${fieldIdPrefix}-streetAddress1`}
          name='streetAddress1'
          onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
            handleAddressChange('streetAddress1', e.target.value)
          }
          label={translatedText.address.labelStreetAddress}
          value={address?.streetAddress1 ?? ''}
          required
          error={showErrors && (address?.streetAddress1 == null || address?.streetAddress1 === '')}
          inputProps={{ maxLength: 1000 }}
          fullWidth
        />
        {address?.streetAddress1 != null &&
          address.streetAddress1 !== '' &&
          address.streetAddress1.length >= 1000 && <CharacterLimitHelperText limit={1000} />}
      </Grid>

      <Grid
        size={12}
        sx={{
          ...(!showAddress2 && {
            marginTop: -1,
          }),
        }}
      >
        {showAddress2 ? (
          <>
            <TextField
              id={`${fieldIdPrefix}-streetAddress2`}
              name='streetAddress2'
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                handleAddressChange('streetAddress2', e.target.value)
              }
              label={translatedText.address.labelStreetAddress2}
              value={address?.streetAddress2 ?? ''}
              inputProps={{ maxLength: 500 }}
              fullWidth
            />

            {address?.streetAddress2 != null &&
              address.streetAddress2 !== '' &&
              address.streetAddress2.length >= 500 && <CharacterLimitHelperText limit={500} />}
          </>
        ) : (
          <Button variant='secondary' size='small' onClick={handleAddAddress2}>
            {translatedText.address.addAddress2}
          </Button>
        )}
      </Grid>

      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          id={`${fieldIdPrefix}-city`}
          key={'city'}
          name='city'
          onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
            handleAddressChange('city', e.target.value)
          }
          label={translatedText.address.labelCity}
          value={address?.city ?? ''}
          error={showErrors && (address?.city == null || address?.city === '')}
          required
          inputProps={{ maxLength: 500 }}
          fullWidth
        />
        {address?.city != null && address.city !== '' && address.city.length >= 500 && (
          <CharacterLimitHelperText limit={500} />
        )}
      </Grid>

      <Grid size={{ xs: 12, sm: 6 }}>
        <SelectInput
          required
          disabled={disabled}
          id='country-select'
          hasError={showErrors && address?.countryId == null}
          label={translatedText.address.labelCountry}
          value={address?.countryId != null ? address.countryId.toString() : ''}
          selectOptions={countrySelectOptions}
          onChange={(e: SelectChangeEvent) => {
            const value = Number(e.target.value);
            if (address?.countryId != value) {
              e.preventDefault();
              e.stopPropagation();

              handleAddressChange('countryId', value);
            }
          }}
        />
      </Grid>

      {showCountyAndState && (
        <>
          <Grid size={{ xs: 12, sm: 6 }}>
            <SelectInput
              disabled={disabled}
              required
              hasError={showErrors && address?.stateId == null}
              id='state-select'
              label={translatedText.address.labelState}
              value={address?.stateId ?? ''}
              selectOptions={stateSelectOptions}
              onChange={(e: SelectChangeEvent) => {
                updateStateId(e.target.value);
              }}
            />
          </Grid>

          {!hideCounty && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <SelectInput
                required
                disabled={disabled}
                id='county-select'
                hasError={showErrors && address?.countyId == null}
                label={translatedText.address.labelCounty}
                value={address?.countyId ?? ''}
                selectOptions={shippingCountiesInStateOptions.map((county: CountyProps) => {
                  return { label: county.name, value: county.id };
                })}
                onChange={(e: SelectChangeEvent) => handleAddressChange('countyId', e.target.value)}
              />
            </Grid>
          )}
        </>
      )}

      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          id={`${fieldIdPrefix}-zipCode`}
          key={'zipCode'}
          name='zipCode'
          required
          label={translatedText.address.labelZipCode}
          value={address?.zipCode ?? ''}
          error={zipCodeError}
          onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
            handleAddressChange('zipCode', e.target.value)
          }
          inputProps={{
            maxLength: address?.countryId !== USCountryId ? 10 : 5,
            error: zipCodeError,
          }}
          fullWidth
        />
      </Grid>
    </Grid>
  );
}
